import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Tabs } from '../components/ui/Tabs';
import { StatusBadge } from '../components/StatusBadge';
import { OrderTimeline } from '../components/OrderTimeline';
import { OrderDocuments } from '../components/OrderDocuments';
import { OrderNotes } from '../components/OrderNotes';
import { mockOrders, mockStatusHistory, mockDocuments, mockNotes, mockPayoffCompanies, mockInsuranceCompanies, mockAgentContacts, mockTitleCompanies, mockTitleContacts, mockLenders } from '../data/mockData';
import { OrderStatus, DocumentFolder } from '../types';
export function OrderDetail() {
  const {
    orderId
  } = useParams();
  const navigate = useNavigate();
  const order = mockOrders.find(o => o.id === orderId);
  const orderHistory = mockStatusHistory.filter(h => h.orderId === orderId);
  const orderDocuments = mockDocuments.filter(d => d.orderId === orderId);
  const initialNotes = mockNotes.filter(n => n.orderId === orderId);
  const [orderNotes, setOrderNotes] = useState(initialNotes);
  const [currentStatus, setCurrentStatus] = useState<OrderStatus>(order?.status || 'ordered');
  if (!order) {
    return <div className="text-center py-12" data-id="element-440">
        <p className="text-slate-600" data-id="element-441">Order not found</p>
        <Button variant="secondary" onClick={() => navigate('/orders')} className="mt-4" data-id="element-442">
          Back to Orders
        </Button>
      </div>;
  }
  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const statusOptions = [{
    value: 'ordered',
    label: 'Ordered'
  }, {
    value: 'pending',
    label: 'Pending'
  }, {
    value: 'received',
    label: 'Received'
  }, {
    value: 'reviewed',
    label: 'Reviewed'
  }, {
    value: 'complete',
    label: 'Complete'
  }];
  const handleStatusChange = (newStatus: OrderStatus) => {
    setCurrentStatus(newStatus);
  };
  const handleDocumentUpload = (file: File, folder: DocumentFolder) => {
    console.log('Uploading file:', file.name, 'to folder:', folder);
  };
  const handleDocumentDelete = (documentId: string) => {
    console.log('Deleting document:', documentId);
  };
  const handleAddNote = (content: string) => {
    const newNote = {
      id: `note-${Date.now()}`,
      orderId: orderId || '',
      content,
      author: 'Current User',
      createdAt: new Date().toISOString()
    };
    setOrderNotes(prev => [newNote, ...prev]);
    mockNotes.push(newNote);
  };
  const payoffCompany = order.payoffCompanyId ? mockPayoffCompanies.find(c => c.id === order.payoffCompanyId) : null;
  const insuranceCompany = order.insuranceCompanyId ? mockInsuranceCompanies.find(c => c.id === order.insuranceCompanyId) : null;
  const agentContact = order.agentContactId ? mockAgentContacts.find(a => a.id === order.agentContactId) : null;
  const chosenLender = order.chosenLenderId ? mockLenders.find(l => l.id === order.chosenLenderId) : null;
  const effectiveDateLabel = order.loanType === 'Purchase' ? 'HOI Effective Date (Close Date)' : 'HOI Effective Date (Ordered Date)';
  const titleCompany = order.titleCompanyId ? mockTitleCompanies.find(c => c.id === order.titleCompanyId) : null;
  const titleContact = order.titleContactId ? mockTitleContacts.find(c => c.id === order.titleContactId) : null;
  const titleLender = order.orderType === 'title' && order.chosenLenderId ? mockLenders.find(l => l.id === order.chosenLenderId) : null;
  const tabs = [{
    id: 'timeline',
    label: 'Timeline',
    content: <OrderTimeline history={orderHistory} data-id="element-443" />
  }, {
    id: 'documents',
    label: 'Documents',
    content: <OrderDocuments documents={orderDocuments} onUpload={handleDocumentUpload} onDelete={handleDocumentDelete} data-id="element-444" />
  }, {
    id: 'notes',
    label: 'Notes',
    content: <OrderNotes notes={orderNotes} onAddNote={handleAddNote} data-id="element-445" />
  }];
  return <div className="space-y-6" data-id="element-446">
      <div className="flex items-center justify-between" data-id="element-447">
        <div className="flex items-center space-x-4" data-id="element-448">
          <Button variant="ghost" onClick={() => navigate('/orders')} className="p-2" data-id="element-449">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-450" />
          </Button>
          <div data-id="element-451">
            <h1 className="text-3xl font-bold text-slate-900" data-id="element-452">
              {order.loanNumber}
            </h1>
            <p className="text-slate-600 mt-1" data-id="element-453">{order.borrowerName}</p>
          </div>
        </div>
        <Button variant="secondary" data-id="element-454">
          <EditIcon className="w-4 h-4 mr-2" data-id="element-455" />
          Edit Order
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-456">
        <div className="lg:col-span-2 space-y-6" data-id="element-457">
          <Card className="p-6" data-id="element-458">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-459">
              Order Details
            </h2>
            <div className="grid grid-cols-2 gap-4" data-id="element-460">
              <div data-id="element-461">
                <p className="text-sm text-slate-600 mb-1" data-id="element-462">Order Type</p>
                <p className="text-sm font-medium text-slate-900 capitalize" data-id="element-463">
                  {order.orderType}
                </p>
              </div>
              <div data-id="element-464">
                <p className="text-sm text-slate-600 mb-1" data-id="element-465">Status</p>
                <StatusBadge status={currentStatus} data-id="element-466" />
              </div>
              <div data-id="element-467">
                <p className="text-sm text-slate-600 mb-1" data-id="element-468">Assigned To</p>
                <p className="text-sm font-medium text-slate-900" data-id="element-469">
                  {order.assignedTo}
                </p>
              </div>
              <div data-id="element-470">
                <p className="text-sm text-slate-600 mb-1" data-id="element-471">Created</p>
                <p className="text-sm font-medium text-slate-900" data-id="element-472">
                  {formatDate(order.createdAt)}
                </p>
              </div>
            </div>

            {order.orderType === 'payoff' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-473">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-474">
                  Payoff Information
                </h3>

                {/* Dates */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-475">
                  <div data-id="element-476">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-477">Ordered Date</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-478">
                      {formatDate(order.payoffOrderedDate || '')}
                    </p>
                  </div>
                  <div data-id="element-479">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-480">
                      Completed Date
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-481">
                      {formatDate(order.payoffCompletedDate || '')}
                    </p>
                  </div>
                  <div data-id="element-482">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-483">
                      Good Thru Date
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-484">
                      {formatDate(order.goodThroughDate || '')}
                    </p>
                  </div>
                </div>

                {/* Loan Details */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-485">
                  <div data-id="element-486">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-487">
                      Existing Loan #
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-488">
                      {order.existingLoanNumber || '—'}
                    </p>
                  </div>
                  <div data-id="element-489">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-490">Lien Position</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-491">
                      {order.lienPosition || '—'}
                    </p>
                  </div>
                  <div data-id="element-492">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-493">
                      Liability Type
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-494">
                      {order.liabilityType || '—'}
                    </p>
                  </div>
                </div>

                {/* Payoff Company */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-495">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-496">
                    Payoff Company
                  </p>
                  <div className="grid grid-cols-2 gap-4" data-id="element-497">
                    <div data-id="element-498">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-499">Company</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-500">
                        {payoffCompany?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-501">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-502">
                        Is Automated
                      </p>
                      {order.isAutomated ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-503">
                          <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-504" />
                          Yes
                        </span> : <span className="inline-flex items-center text-sm font-medium text-slate-500" data-id="element-505">
                          <XCircleIcon className="w-4 h-4 mr-1" data-id="element-506" />
                          No
                        </span>}
                    </div>
                    <div data-id="element-507">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-508">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-509">
                        {order.payoffCompanyPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-510">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-511">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-512">
                        {order.payoffCompanyEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Borrowers Auth Status */}
                <div className="mt-4" data-id="element-513">
                  <p className="text-sm text-slate-600 mb-1" data-id="element-514">
                    Borrowers Authorization
                  </p>
                  {order.borrowersAuthAttached ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-515">
                      <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-516" />
                      Attached
                    </span> : <span className="inline-flex items-center text-sm font-medium text-amber-600" data-id="element-517">
                      <XCircleIcon className="w-4 h-4 mr-1" data-id="element-518" />
                      Not attached — required before send
                    </span>}
                </div>
              </div>}

            {order.orderType === 'insurance' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-519">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-520">
                  Insurance Information
                </h3>

                {/* Type & Loan Type */}
                <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-521">
                  <div data-id="element-522">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-523">
                      Insurance Type
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-524">
                      {order.insuranceType}
                    </p>
                  </div>
                  <div data-id="element-525">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-526">Loan Type</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-527">
                      {order.loanType || '—'}
                    </p>
                  </div>
                  <div data-id="element-528">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-529">
                      {effectiveDateLabel}
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-530">
                      {formatDate(order.hoiEffectiveDate || '')}
                    </p>
                  </div>
                </div>

                {/* Insurance Company */}
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-531">
                  <div data-id="element-532">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-533">
                      Insurance Company
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-534">
                      {insuranceCompany?.name || '—'}
                    </p>
                  </div>
                  {order.policyNumber && <div data-id="element-535">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-536">
                        Policy Number
                      </p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-537">
                        {order.policyNumber}
                      </p>
                    </div>}
                </div>

                {/* Agent Contact */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-538">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-539">
                    Agent Contact
                  </p>
                  <div className="grid grid-cols-3 gap-4" data-id="element-540">
                    <div data-id="element-541">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-542">Name</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-543">
                        {agentContact?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-544">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-545">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-546">
                        {order.agentPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-547">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-548">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-549">
                        {order.agentEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mortgagee Clause */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-550">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-551">
                    Mortgagee Clause
                  </p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-552">
                    {order.mortgageeClause || '—'}
                  </p>
                  {chosenLender && <p className="text-xs text-slate-500 mt-1" data-id="element-553">
                      From: {chosenLender.name}
                    </p>}
                </div>

                {/* Borrowers Auth Status */}
                <div data-id="element-554">
                  <p className="text-sm text-slate-600 mb-1" data-id="element-555">
                    Borrowers Authorization
                  </p>
                  {order.insuranceBorrowersAuthAttached ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-556">
                      <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-557" />
                      Attached
                    </span> : <span className="inline-flex items-center text-sm font-medium text-amber-600" data-id="element-558">
                      <XCircleIcon className="w-4 h-4 mr-1" data-id="element-559" />
                      Not attached — required before send
                    </span>}
                </div>
              </div>}

            {order.orderType === 'title' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-560">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-561">
                  Title Information
                </h3>

                {/* Opportunity Fields */}
                <div className="grid grid-cols-2 gap-4 mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-562">
                  <div data-id="element-563">
                    <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-564">
                      DOB (from Opportunity)
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-565">
                      {formatDate(order.borrowerDob || '')}
                    </p>
                  </div>
                  <div data-id="element-566">
                    <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-567">
                      Last 4 SSN (from Opportunity)
                    </p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-568">
                      {order.borrowerLast4SSN ? `••••${order.borrowerLast4SSN}` : '—'}
                    </p>
                  </div>
                </div>

                {/* Mortgagee Clause */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 mb-4" data-id="element-569">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-570">
                    Mortgagee Clause
                  </p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-571">
                    {order.titleMortgageeClause || '—'}
                  </p>
                  {titleLender && <p className="text-xs text-slate-500 mt-1" data-id="element-572">
                      From: {titleLender.name}
                    </p>}
                </div>

                {/* Title Company */}
                <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-573">
                  <div data-id="element-574">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-575">Title Company</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-576">
                      {titleCompany?.name || '—'}
                    </p>
                  </div>
                </div>

                {/* Title Contact */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-577">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3" data-id="element-578">
                    Title Contact
                  </p>
                  <div className="grid grid-cols-3 gap-4" data-id="element-579">
                    <div data-id="element-580">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-581">Name</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-582">
                        {titleContact?.name || '—'}
                      </p>
                    </div>
                    <div data-id="element-583">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-584">Phone</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-585">
                        {order.titleContactPhone || '—'}
                      </p>
                    </div>
                    <div data-id="element-586">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-587">Email</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-588">
                        {order.titleContactEmail || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>}

            {order.orderType === 'appraisal' && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-589">
                <h3 className="text-md font-medium text-slate-900 mb-3" data-id="element-590">
                  Appraisal Information
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-591">
                  <div data-id="element-592">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-593">Appraiser</p>
                    <p className="text-sm font-medium text-slate-900" data-id="element-594">
                      {order.appraiser}
                    </p>
                  </div>
                  {order.appraisalOrderDate && <div data-id="element-595">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-596">Order Date</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-597">
                        {formatDate(order.appraisalOrderDate)}
                      </p>
                    </div>}
                  {order.propertyAddress && <div className="col-span-2" data-id="element-598">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-599">
                        Property Address
                      </p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-600">
                        {order.propertyAddress}
                      </p>
                    </div>}
                  {order.valuLinkId && <div data-id="element-601">
                      <p className="text-sm text-slate-600 mb-1" data-id="element-602">ValuLink ID</p>
                      <p className="text-sm font-medium text-slate-900" data-id="element-603">
                        {order.valuLinkId}
                      </p>
                    </div>}
                </div>
              </div>}
          </Card>

          <Card className="p-6" data-id="element-604">
            <Tabs tabs={tabs} data-id="element-605" />
          </Card>
        </div>

        <div className="space-y-6" data-id="element-606">
          <Card className="p-6" data-id="element-607">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-608">
              Update Status
            </h2>
            <Select options={statusOptions} value={currentStatus} onChange={e => handleStatusChange(e.target.value as OrderStatus)} data-id="element-609" />
            <p className="text-xs text-slate-500 mt-2" data-id="element-610">
              Status changes are automatically timestamped and tracked
            </p>
          </Card>

          <Card className="p-6" data-id="element-611">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-612">
              Quick Stats
            </h2>
            <div className="space-y-3" data-id="element-613">
              <div className="flex justify-between items-center" data-id="element-614">
                <span className="text-sm text-slate-600" data-id="element-615">Documents</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-616">
                  {orderDocuments.length}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-617">
                <span className="text-sm text-slate-600" data-id="element-618">Notes</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-619">
                  {orderNotes.length}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-620">
                <span className="text-sm text-slate-600" data-id="element-621">Status Updates</span>
                <span className="text-sm font-medium text-slate-900" data-id="element-622">
                  {orderHistory.length}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}